import { useCallback, useEffect, useRef, useState } from "react"

import type { StorageScope } from "@/utils/scopedStorageKey"

import { useScopedStorageKey } from "./useScopedStorageKey"

function readValue<T>(key: string | undefined, fallback: T): T {
  if (typeof window === "undefined" || !key) {
    return fallback
  }
  try {
    const raw = window.localStorage.getItem(key)
    return raw ? (JSON.parse(raw) as T) : fallback
  } catch {
    return fallback
  }
}

function writeValue<T>(key: string | undefined, value: T): void {
  if (typeof window === "undefined" || !key) {
    return
  }
  try {
    window.localStorage.setItem(key, JSON.stringify(value))
  } catch {
    // Ignore storage failures (private mode / quota); value still lives in
    // memory for the rest of the session.
  }
}

/**
 * Like `useLocalStorage`, but the key is scoped by org (and, by default,
 * workspace) via `useScopedStorageKey` — use this instead of `useLocalStorage`
 * for any org- or workspace-specific cache. Re-hydrates when the active
 * org/workspace changes, not just on mount, so a client-side workspace switch
 * can't leave state hydrated under the previous scope. Persistence is a no-op
 * until the organization resolves (matches every other scoped-storage caller
 * in this codebase).
 */
export function useScopedLocalStorage<T>(
  base: string,
  initialValue: T,
  scope?: StorageScope,
): [T, (value: T) => void] {
  const scopedKey = useScopedStorageKey(base, scope)
  const [value, setLocalValue] = useState<T>(() =>
    readValue(scopedKey, initialValue),
  )
  const keyRef = useRef(scopedKey)
  const initializedForKeyRef = useRef(scopedKey)
  const initialValueRef = useRef(initialValue)
  initialValueRef.current = initialValue

  useEffect(() => {
    keyRef.current = scopedKey
    if (initializedForKeyRef.current === scopedKey) {
      return
    }
    initializedForKeyRef.current = scopedKey
    setLocalValue(readValue(scopedKey, initialValueRef.current))
  }, [scopedKey])

  const setValue = useCallback((next: T) => {
    setLocalValue(next)
    writeValue(keyRef.current, next)
  }, [])

  return [value, setValue]
}
